import { useState, useMemo, useCallback, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useCalendarEvents, useReschedulePost } from "@/hooks/use-calendar";
import { useAccounts } from "@/hooks/use-accounts";
import { CalendarFilters } from "@/components/calendar/calendar-filters";
import { UnscheduledQueue } from "@/components/calendar/unscheduled-queue";
import { EventCard, RESCHEDULE_DND_TYPE, DROP_RING, EVENT_CARD_MARKER, isCalendarDragType } from "@/components/calendar/event-card";
import { TimeSlotCell } from "@/components/calendar/time-slot-cell";
import { PostDetailModal } from "@/components/calendar/post-detail-modal";
import { ScheduleModal } from "@/components/calendar/schedule-modal";
import { StatusBar } from "@/components/calendar/status-bar";
import { Button } from "@/components/ui/button";
import { useAssistantStore } from "@/stores/assistant-store";
import { CalendarClock, ChevronLeft, ChevronRight, Plus, Sparkles, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CreateMenu } from "@/components/create/create-menu";
import { viewerTimeZone, instantToLocalParts, wallTimeToInstant } from "@content-os/scheduling";
import { isPastDay, isPastHour, minuteFromOffset, type NowParts } from "./calendar-past";
import { MIN_SCHEDULE_LEAD_MS } from "@content-os/config";
import type { CalendarEvent, UnscheduledItem } from "@content-os/types";
import type { SocialPlatform, ScheduledPostStatus } from "@content-os/config";

/** Shift a "YYYY-MM-DD" calendar date by `n` days via UTC math (TZ-agnostic). */
function addDaysKey(dateKey: string, n: number): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** The UTC instant for midnight (start of day) of `dateKey` in `tz`. */
function dayStartInstant(dateKey: string, tz: string): Date {
  const [y, m, d] = dateKey.split("-").map(Number);
  return wallTimeToInstant(y, m, d, 0, 0, tz) ?? new Date(dateKey + "T00:00:00Z");
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

/**
 * Drag-over / drop handlers for a droppable calendar slot. Highlights the cell
 * on hover via direct DOM classList (no re-render churn while dragging) and
 * delegates the reschedule on drop. `hour` is null for day-level (month) drops.
 * (Week/day time cells use TimeSlotCell, which adds the 15-min guide line.)
 */
function slotDropProps(
  onSlotDrop: (dateKey: string, hour: number | null, minute: number, e: DragEvent) => void,
  dateKey: string,
  hour: number | null,
) {
  return {
    onDragOver: (e: DragEvent) => {
      // Highlight for both a reschedule drag and an unscheduled-queue item.
      if (!isCalendarDragType(e.dataTransfer.types)) return;
      e.preventDefault();
      e.currentTarget.classList.add(...DROP_RING);
    },
    onDragLeave: (e: DragEvent) => e.currentTarget.classList.remove(...DROP_RING),
    onDrop: (e: DragEvent) => {
      e.currentTarget.classList.remove(...DROP_RING);
      // Day-level (month) drops have no hour and so no sub-hour minute.
      const rect = e.currentTarget.getBoundingClientRect();
      const minute = hour === null ? 0 : minuteFromOffset(e.clientY - rect.top, rect.height);
      onSlotDrop(dateKey, hour, minute, e);
    },
  };
}

export function BrandCalendarPage() {
  const { brand } = useBrandContext();
  const { canSchedule } = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation("calendar");
  const { t: tNav } = useTranslation("nav");
  const locale = useLocale();
  const reschedule = useReschedulePost(brand.id);
  // Calendar times render in the viewer's own timezone (posts are stored as
  // UTC instants). The brand scheduling-config timezone governs only the
  // server-side auto-suggest posting windows, not what is displayed here.
  const tz = useMemo(() => viewerTimeZone(), []);
  // Viewer-local parts of "now" for past-slot shading — one timezone conversion
  // per render, then compared numerically per cell (see calendar-past.ts).
  const nowParts: NowParts = instantToLocalParts(new Date(), tz);

  const [view, setView] = useState<"month" | "week" | "day">("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<ScheduledPostStatus[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  // Scheduling a queue item (piece) — opened by queue click or drop-on-day.
  const [scheduleTarget, setScheduleTarget] = useState<{
    pieceId: string;
    title?: string;
    defaultFor?: string;
  } | null>(null);

  // Range for the API query, computed in the brand's timezone. The server
  // filters with $lte, so `to` is the last visible instant (1ms before the
  // start of the day after the range) — inclusive end.
  const dateRange = useMemo(() => {
    const cur = instantToLocalParts(currentDate, tz);
    const inclusiveEnd = (exclusiveStart: Date) => new Date(exclusiveStart.getTime() - 1);
    if (view === "month") {
      const firstKey = `${cur.dateKey.slice(0, 8)}01`;
      const nextMonthKey =
        cur.month === 12
          ? `${cur.year + 1}-01-01`
          : `${cur.year}-${String(cur.month + 1).padStart(2, "0")}-01`;
      return {
        from: dayStartInstant(firstKey, tz),
        to: inclusiveEnd(dayStartInstant(nextMonthKey, tz)),
      };
    }
    if (view === "day") {
      return {
        from: dayStartInstant(cur.dateKey, tz),
        to: inclusiveEnd(dayStartInstant(addDaysKey(cur.dateKey, 1), tz)),
      };
    }
    // Week: the Sun-based week containing currentDate.
    const firstKey = addDaysKey(cur.dateKey, -cur.dayOfWeek);
    return {
      from: dayStartInstant(firstKey, tz),
      to: inclusiveEnd(dayStartInstant(addDaysKey(firstKey, 7), tz)),
    };
  }, [view, currentDate, tz]);

  const { data: accounts = [] } = useAccounts(brand.id);
  const availablePlatforms = useMemo(() => {
    const set = new Set<SocialPlatform>();
    for (const a of accounts) {
      if (a.status === "ACTIVE") set.add(a.platform);
    }
    return Array.from(set);
  }, [accounts]);

  const { data: events = [], isLoading } = useCalendarEvents(brand.id, {
    from: dateRange.from.toISOString(),
    to: dateRange.to.toISOString(),
    platform: selectedPlatforms.length > 0 ? selectedPlatforms.join(",") : undefined,
    status: selectedStatuses.length > 0 ? selectedStatuses.join(",") : undefined,
  });

  const navigateDate = useCallback(
    (direction: "prev" | "next" | "today") => {
      if (direction === "today") {
        setCurrentDate(new Date());
        return;
      }
      const cur = instantToLocalParts(currentDate, tz);
      const delta = direction === "next" ? 1 : -1;
      if (view === "month") {
        // Shift month in brand-TZ calendar, clamping the day to month length.
        let newYear = cur.year;
        let newMonth = cur.month + delta; // 1-12
        if (newMonth < 1) {
          newMonth = 12;
          newYear -= 1;
        } else if (newMonth > 12) {
          newMonth = 1;
          newYear += 1;
        }
        const daysInNewMonth = new Date(Date.UTC(newYear, newMonth, 0)).getUTCDate();
        const newDay = Math.min(cur.day, daysInNewMonth);
        const newKey = `${newYear}-${String(newMonth).padStart(2, "0")}-${String(newDay).padStart(2, "0")}`;
        setCurrentDate(dayStartInstant(newKey, tz));
      } else {
        const days = view === "week" ? 7 * delta : delta;
        setCurrentDate(dayStartInstant(addDaysKey(cur.dateKey, days), tz));
      }
    },
    [currentDate, view, tz],
  );

  const togglePlatform = (p: SocialPlatform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    );
  };

  const toggleStatus = (s: ScheduledPostStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  };

  // Handle a queue item. Pieces open the schedule modal. Raw uploads can't be
  // scheduled as-is, so we route to Quick Post prefilled with the media (and the
  // dropped date, if any) — "add them through a post". `defaultFor` is an ISO
  // instant to prefill the time with (set when dropped onto a calendar day).
  const openScheduleFor = useCallback(
    (item: UnscheduledItem, defaultFor?: string) => {
      if (item.type !== "piece") {
        navigate(`/${brand.slug}/quick-post`, {
          state: {
            mediaUrl: item.mediaUrls?.[0],
            mediaType: item.mediaType,
            scheduledFor: defaultFor,
          },
        });
        return;
      }
      setScheduleTarget({ pieceId: item.id, title: item.title, defaultFor });
    },
    [navigate, brand.slug],
  );

  const handleQueueItemClick = (item: UnscheduledItem) => openScheduleFor(item);

  // Handle a drop on a calendar slot — either rescheduling an existing post
  // (RESCHEDULE_DND_TYPE) or scheduling an unscheduled-queue item dropped on a
  // day (application/json). Month drops are day-level; week/day set the hour.
  const handleSlotDrop = useCallback(
    (dateKey: string, hour: number | null, minute: number, e: DragEvent) => {
      e.preventDefault();
      const [y, m, d] = dateKey.split("-").map(Number);

      const postId = e.dataTransfer.getData(RESCHEDULE_DND_TYPE);
      if (postId) {
        const ev = events.find((x) => x.id === postId);
        let target: Date | null;
        if (hour !== null) {
          target = wallTimeToInstant(y, m, d, hour, minute, tz); // set hour + 15-min quarter (brand TZ)
        } else if (ev) {
          const op = instantToLocalParts(new Date(ev.scheduledFor), tz); // preserve time
          target = wallTimeToInstant(y, m, d, op.hour, op.minute, tz);
        } else {
          return; // month drop but the post isn't in view — can't preserve its time
        }
        if (!target) return; // wall time falls in a DST spring-forward gap
        // Too soon — the scheduler needs lead time; the API would reject it too.
        if (target.getTime() < Date.now() + MIN_SCHEDULE_LEAD_MS) return;
        const min = (ms: number) => Math.floor(ms / 60000);
        if (ev && min(new Date(ev.scheduledFor).getTime()) === min(target.getTime())) return;
        reschedule.mutate({ postId, data: { scheduledFor: target.toISOString() } });
        return;
      }

      // Unscheduled-queue item dropped onto a day → open the schedule modal
      // prefilled with the slot (default 9am for day-level month drops).
      const queueRaw = e.dataTransfer.getData("application/json");
      if (!queueRaw) return;
      let item: UnscheduledItem;
      try {
        item = JSON.parse(queueRaw);
      } catch {
        return;
      }
      // Only prefill the dropped time when it clears the lead time; a too-soon
      // (or past-day) drop opens the create flow without a bad prefilled date.
      const slot = wallTimeToInstant(y, m, d, hour ?? 9, hour === null ? 0 : minute, tz);
      const defaultFor =
        slot && slot.getTime() >= Date.now() + MIN_SCHEDULE_LEAD_MS
          ? slot.toISOString()
          : undefined;
      openScheduleFor(item, defaultFor);
    },
    [events, reschedule, tz, openScheduleFor],
  );

  // Click an empty (future) slot → open Quick Post with that instant prefilled.
  // Day-level (month) clicks default to 9am; the prefill is dropped if the slot
  // is in the past or within the scheduler's lead time.
  const handleSlotCreate = useCallback(
    (dateKey: string, hour: number | null, minute: number) => {
      const [y, m, d] = dateKey.split("-").map(Number);
      const slot = wallTimeToInstant(y, m, d, hour ?? 9, hour === null ? 0 : minute, tz);
      const scheduledFor =
        slot && slot.getTime() >= Date.now() + MIN_SCHEDULE_LEAD_MS ? slot.toISOString() : undefined;
      navigate(`/${brand.slug}/quick-post`, { state: { scheduledFor } });
    },
    [tz, navigate, brand.slug],
  );

  const viewTitle = useMemo(() => {
    if (view === "month") {
      return formatDate(currentDate, locale, { month: "long", year: "numeric", timeZone: tz });
    }
    if (view === "day") {
      return formatDate(currentDate, locale, { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: tz });
    }
    const cur = instantToLocalParts(currentDate, tz);
    const firstKey = addDaysKey(cur.dateKey, -cur.dayOfWeek);
    const from = dayStartInstant(firstKey, tz);
    const to = dayStartInstant(addDaysKey(firstKey, 6), tz);
    return `${formatDate(from, locale, { month: "short", day: "numeric", timeZone: tz })} – ${formatDate(to, locale, { month: "short", day: "numeric", year: "numeric", timeZone: tz })}`;
  }, [view, currentDate, locale, tz]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      const day = instantToLocalParts(new Date(ev.scheduledFor), tz).dateKey;
      const arr = map.get(day) ?? [];
      arr.push(ev);
      map.set(day, arr);
    }
    return map;
  }, [events, tz]);

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold">{t("page.title")}</h1>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigateDate("prev")}>
              <ChevronLeft size={14} />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigateDate("today")}>
              {t("page.today")}
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigateDate("next")}>
              <ChevronRight size={14} />
            </Button>
          </div>
          <span className="text-sm font-medium">{viewTitle}</span>
          <span
            className="hidden items-center gap-1 text-xs text-muted-foreground sm:inline-flex"
            title={t("page.timezone", { tz })}
          >
            <CalendarClock size={12} /> {tz}
          </span>
        </div>
        {canSchedule && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/${brand.slug}/calendar/upload`)}
            >
              <Upload size={14} className="mr-1" /> {t("page.upload")}
            </Button>
            <CreateMenu brandSlug={brand.slug}>
              <Button size="sm">
                <Plus size={14} className="mr-1" /> {tNav("create.post")}
              </Button>
            </CreateMenu>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="px-4 py-2 border-b">
        <CalendarFilters
          selectedPlatforms={selectedPlatforms}
          onPlatformToggle={togglePlatform}
          selectedStatuses={selectedStatuses}
          onStatusToggle={toggleStatus}
          view={view}
          onViewChange={setView}
          availablePlatforms={availablePlatforms}
        />
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        <UnscheduledQueue
          brandId={brand.id}
          onItemClick={handleQueueItemClick}
        />

        <div className="relative flex-1 overflow-auto">
          {!isLoading && events.length === 0 && (() => {
            const isFiltered = selectedPlatforms.length > 0 || selectedStatuses.length > 0;
            return (
              <div className="pointer-events-none absolute inset-x-0 top-12 z-10 flex justify-center px-4">
                <div className="pointer-events-auto flex flex-col items-center gap-2 rounded-lg border bg-card p-4 text-center shadow-md">
                  <CalendarClock size={24} className="text-muted-foreground/60" />
                  {isFiltered ? (
                    <p className="text-sm font-medium">{t("page.emptyFiltered")}</p>
                  ) : (
                    <>
                      <p className="text-sm font-medium">{t("page.emptyScheduled")}</p>
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <CreateMenu brandSlug={brand.slug} align="center">
                          <Button size="sm">
                            <Plus size={14} className="mr-1.5" /> {tNav("create.post")}
                          </Button>
                        </CreateMenu>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            useAssistantStore
                              .getState()
                              .openWithPrompt(
                                "Plan my posting schedule for the next 7 days. Check approved pieces and suggest slots.",
                              )
                          }
                        >
                          <Sparkles size={14} className="mr-1.5" /> {t("page.planWeek")}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}
          {isLoading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="h-16 rounded bg-muted animate-pulse" />
              ))}
            </div>
          ) : view === "week" ? (
            <WeekView currentDate={currentDate} tz={tz} nowParts={nowParts} events={events} onEventClick={setSelectedEvent} onSlotDrop={handleSlotDrop} onSlotCreate={canSchedule ? handleSlotCreate : undefined} />
          ) : view === "month" ? (
            <MonthView currentDate={currentDate} tz={tz} nowParts={nowParts} eventsByDay={eventsByDay} onEventClick={setSelectedEvent} onSlotDrop={handleSlotDrop} onSlotCreate={canSchedule ? handleSlotCreate : undefined} />
          ) : (
            <DayView currentDate={currentDate} tz={tz} nowParts={nowParts} events={events} onEventClick={setSelectedEvent} onSlotDrop={handleSlotDrop} onSlotCreate={canSchedule ? handleSlotCreate : undefined} />
          )}
        </div>
      </div>

      <StatusBar events={events} />

      <PostDetailModal
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        brandId={brand.id}
        canSchedule={canSchedule}
      />

      <ScheduleModal
        isOpen={!!scheduleTarget}
        onClose={() => setScheduleTarget(null)}
        brandId={brand.id}
        pieceId={scheduleTarget?.pieceId ?? ""}
        pieceCaption={scheduleTarget?.title}
        connectedPlatforms={availablePlatforms}
        defaultScheduledFor={scheduleTarget?.defaultFor}
        tz={tz}
      />
    </div>
  );
}

// --- Week View ---
function WeekView({
  currentDate, tz, nowParts, events, onEventClick, onSlotDrop, onSlotCreate,
}: {
  currentDate: Date; tz: string; nowParts: NowParts; events: CalendarEvent[]; onEventClick: (e: CalendarEvent) => void;
  onSlotDrop: (dateKey: string, hour: number | null, minute: number, e: DragEvent) => void;
  onSlotCreate?: (dateKey: string, hour: number | null, minute: number) => void;
}) {
  const { t } = useTranslation("calendar");
  const todayKey = nowParts.dateKey;
  // 7 day columns as brand-TZ date keys (Sun-based week containing currentDate).
  const dayKeys = useMemo(() => {
    const cur = instantToLocalParts(currentDate, tz);
    const firstKey = addDaysKey(cur.dateKey, -cur.dayOfWeek);
    return Array.from({ length: 7 }, (_, i) => addDaysKey(firstKey, i));
  }, [currentDate, tz]);

  const eventsByDayHour = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      const p = instantToLocalParts(new Date(ev.scheduledFor), tz);
      const key = `${p.dateKey}-${p.hour}`;
      const arr = map.get(key) ?? [];
      arr.push(ev);
      map.set(key, arr);
    }
    return map;
  }, [events, tz]);

  return (
    <div className="grid grid-cols-[60px_repeat(7,1fr)] min-h-full">
      <div className="border-b" />
      {dayKeys.map((dateKey) => {
        const p = instantToLocalParts(dayStartInstant(dateKey, tz), tz);
        const isToday = dateKey === todayKey;
        const past = isPastDay(dateKey, nowParts);
        return (
          <div
            key={dateKey}
            className={`border-b border-l px-2 py-1.5 text-center${past ? " bg-muted/40" : ""}`}
          >
            <div className={`text-xs ${past ? "text-muted-foreground/60" : "text-muted-foreground"}`}>
              {t(`weekdays.${WEEKDAY_KEYS[p.dayOfWeek]}`)}
            </div>
            <div
              className={`text-sm font-medium${isToday ? " text-primary font-semibold" : past ? " text-muted-foreground/60" : ""}`}
            >
              {p.day}
            </div>
          </div>
        );
      })}
      {HOURS.map((hour) => (
        <div key={hour} className="contents">
          <div className="text-xs text-muted-foreground text-right pr-2 pt-1 h-16 border-b">
            {hour === 0 ? t("hours.midnight") : hour < 12 ? t("hours.am", { hour }) : hour === 12 ? t("hours.noon") : t("hours.pm", { hour: hour - 12 })}
          </div>
          {dayKeys.map((dateKey) => {
            const key = `${dateKey}-${hour}`;
            return (
              <TimeSlotCell
                key={key}
                dateKey={dateKey}
                hour={hour}
                past={isPastHour(dateKey, hour, nowParts)}
                tz={tz}
                compact
                frameClass="border-l border-b h-16 p-0.5"
                events={eventsByDayHour.get(key) ?? []}
                onEventClick={onEventClick}
                onSlotDrop={onSlotDrop}
                onSlotCreate={onSlotCreate}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

// --- Month View ---
function MonthView({
  currentDate, tz, nowParts, eventsByDay, onEventClick, onSlotDrop, onSlotCreate,
}: {
  currentDate: Date; tz: string; nowParts: NowParts; eventsByDay: Map<string, CalendarEvent[]>; onEventClick: (e: CalendarEvent) => void;
  onSlotDrop: (dateKey: string, hour: number | null, minute: number, e: DragEvent) => void;
  onSlotCreate?: (dateKey: string, hour: number | null, minute: number) => void;
}) {
  const { t } = useTranslation("calendar");
  const todayKey = nowParts.dateKey;
  const cur = instantToLocalParts(currentDate, tz);
  const year = cur.year;
  const month = cur.month; // 1-12
  const firstOfMonthKey = `${year}-${String(month).padStart(2, "0")}-01`;
  const firstDay = instantToLocalParts(dayStartInstant(firstOfMonthKey, tz), tz).dayOfWeek;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let i = 1; i <= daysInMonth; i++) cells.push(i);

  const weekdays = WEEKDAY_KEYS.map((key) => t(`weekdays.${key}`));

  return (
    <div className="p-2">
      <div className="grid grid-cols-7 gap-px">
        {weekdays.map((d) => (
          <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>
        ))}
        {cells.map((day, idx) => {
          const dateStr = day ? `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}` : null;
          const dayEvents = dateStr ? eventsByDay.get(dateStr) ?? [] : [];
          const isToday = dateStr === todayKey;
          // Past days are greyed and not droppable; their events stay clickable.
          const past = dateStr ? isPastDay(dateStr, nowParts) : false;

          return (
            <div
              key={idx}
              {...(dateStr && !past ? slotDropProps(onSlotDrop, dateStr, null) : {})}
              onClick={
                dateStr && !past && onSlotCreate
                  ? (e) => {
                      // Clicks on an event open it; empty cell space creates a post.
                      if ((e.target as Element).closest(`[${EVENT_CARD_MARKER}]`)) return;
                      onSlotCreate(dateStr, null, 0);
                    }
                  : undefined
              }
              className={`border rounded-md min-h-[100px] p-1${past ? " bg-muted/40" : dateStr && onSlotCreate ? " cursor-pointer hover:bg-primary/5" : ""}`}
            >
              {day && (
                <>
                  <div
                    className={`text-xs font-medium mb-1${isToday ? " text-primary font-semibold" : past ? " text-muted-foreground/60" : ""}`}
                  >
                    {day}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <EventCard key={ev.id} event={ev} onClick={onEventClick} compact tz={tz} />
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-muted-foreground text-center">
                        {t("page.more", { count: dayEvents.length - 3 })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Day View ---
function DayView({
  currentDate, tz, nowParts, events, onEventClick, onSlotDrop, onSlotCreate,
}: {
  currentDate: Date; tz: string; nowParts: NowParts; events: CalendarEvent[]; onEventClick: (e: CalendarEvent) => void;
  onSlotDrop: (dateKey: string, hour: number | null, minute: number, e: DragEvent) => void;
  onSlotCreate?: (dateKey: string, hour: number | null, minute: number) => void;
}) {
  const { t } = useTranslation("calendar");
  const dayKey = instantToLocalParts(currentDate, tz).dateKey;
  const dayEvents = events.filter(
    (e) => instantToLocalParts(new Date(e.scheduledFor), tz).dateKey === dayKey,
  );

  const eventsByHour = new Map<number, CalendarEvent[]>();
  for (const ev of dayEvents) {
    const hour = instantToLocalParts(new Date(ev.scheduledFor), tz).hour;
    const arr = eventsByHour.get(hour) ?? [];
    arr.push(ev);
    eventsByHour.set(hour, arr);
  }

  return (
    <div className="grid grid-cols-[60px_1fr] min-h-full">
      {HOURS.map((hour) => (
        <div key={hour} className="contents">
          <div className="text-xs text-muted-foreground text-right pr-2 pt-1 h-20 border-b">
            {hour === 0 ? t("hours.midnight") : hour < 12 ? t("hours.am", { hour }) : hour === 12 ? t("hours.noon") : t("hours.pm", { hour: hour - 12 })}
          </div>
          <TimeSlotCell
            dateKey={dayKey}
            hour={hour}
            past={isPastHour(dayKey, hour, nowParts)}
            tz={tz}
            frameClass="border-b h-20 p-1"
            events={eventsByHour.get(hour) ?? []}
            onEventClick={onEventClick}
            onSlotDrop={onSlotDrop}
            onSlotCreate={onSlotCreate}
          />
        </div>
      ))}
    </div>
  );
}
